import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type BillingRunStatus, type BillingRunSummary } from '../lib/api';
import { fmtMonth } from '../lib/fmt';
import {
  Badge, Card, CardHeader, EmptyState, SectionLabel, SortableTh, Spinner,
  Table, Td, Th, Tr,
} from '../components/ui';
import { useTableSort } from '../hooks/useTableSort';

/**
 * "Recent imports" card embedded on the main billing page. Lists the last
 * ~15 runs with status badges + per-row counters; the operator clicks
 * through to the detail page for forensics. Re-queries on a 30s tick so a
 * just-enqueued run appears without a manual refresh.
 */
export function RecentImportsCard() {
  const [runs, setRuns] = useState<BillingRunSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const { runs } = await api.admin.billing.listImports(15);
        if (alive) setRuns(runs);
      } catch (e) {
        if (alive) setError((e as Error).message);
      }
    }
    void load();
    const id = window.setInterval(load, 30_000);
    return () => { alive = false; window.clearInterval(id); };
  }, []);

  if (error) return null; // Quiet failure — the main page already exists
  if (runs === null) return null;
  if (runs.length === 0) return null;

  return (
    <>
      <SectionLabel>Recent imports</SectionLabel>
      <Card>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Last 15 import runs</span>
        </CardHeader>
        <RunsTable runs={runs} />
      </Card>
    </>
  );
}

export function RunsTable({ runs }: { runs: BillingRunSummary[] }) {
  const { sorted, sort } = useTableSort(runs, {
    started: (r: BillingRunSummary) => r.started_at,
    month:   (r: BillingRunSummary) => r.month,
    triggered: (r: BillingRunSummary) => r.triggered_by_display_name ?? r.triggered_by_user_id,
    status:  (r: BillingRunSummary) => r.status,
    created: (r: BillingRunSummary) => r.invoices_created,
    updated: (r: BillingRunSummary) => r.invoices_updated,
    zeroed:  (r: BillingRunSummary) => r.invoices_zeroed_out,
    errors:  (r: BillingRunSummary) => r.accounts_errored,
  }, { key: 'started', dir: 'desc' });
  if (runs.length === 0) return <EmptyState message="No imports yet." />;
  return (
    <Table>
      <thead>
        <tr>
          <SortableTh columnKey="started"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Started</SortableTh>
          <SortableTh columnKey="month"     sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Month</SortableTh>
          <SortableTh columnKey="triggered" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Triggered by</SortableTh>
          <SortableTh columnKey="status"    sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Status</SortableTh>
          <SortableTh columnKey="created"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Created</SortableTh>
          <SortableTh columnKey="updated"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Updated</SortableTh>
          <SortableTh columnKey="zeroed"    sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Zeroed</SortableTh>
          <SortableTh columnKey="errors"    sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Errors</SortableTh>
          <Th> </Th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => (
          <Tr key={r.id}>
            <Td muted style={{ fontSize: 11 }}>{fmtDatetime(r.started_at)}</Td>
            <Td bold>{fmtMonth(r.month)}</Td>
            <Td muted style={{ fontSize: 11 }}>{r.triggered_by_display_name ?? r.triggered_by_user_id ?? '—'}</Td>
            <Td><StatusBadge status={r.status} /></Td>
            <Td right>{r.invoices_created}</Td>
            <Td right>{r.invoices_updated}</Td>
            <Td right>{r.invoices_zeroed_out}</Td>
            <Td right style={{ color: r.accounts_errored > 0 ? 'var(--color-red-text)' : undefined }}>
              {r.accounts_errored}
            </Td>
            <Td>
              <Link to={`/admin/billing/imports/${r.id}`} style={{ color: 'var(--color-blue)', fontSize: 11 }}>
                Details ↗
              </Link>
            </Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}

export function StatusBadge({ status }: { status: BillingRunStatus }) {
  if (status === 'success') return <Badge variant="green">Success</Badge>;
  if (status === 'warning') return <Badge variant="amber">Warning</Badge>;
  if (status === 'failed')  return <Badge variant="red">Failed</Badge>;
  if (status === 'queued')  return <Badge variant="gray">Queued</Badge>;
  return <Badge variant="blue">Running</Badge>;
}

export function fmtDatetime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function AdminBillingRunsList() {
  const [runs, setRuns] = useState<BillingRunSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    api.admin.billing.listImports(200)
      .then(({ runs }) => { if (alive) setRuns(runs); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, []);
  if (error) return <EmptyState message={error} />;
  if (!runs) return <Spinner />;
  return (
    <>
      <SectionLabel>Billing imports</SectionLabel>
      <Card>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>All import runs</span>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
            Showing {runs.length} most recent.
          </span>
        </CardHeader>
        <RunsTable runs={runs} />
      </Card>
    </>
  );
}
